import React from 'react';
import { Box, Typography } from '@mui/material';
import { INK, FONT } from './vizTokens';

/**
 * A headline number.
 *
 * Contract: label (sentence case, no trailing colon) · value · optional note.
 * `accent` is a status colour and only ever arrives with `icon` and a label —
 * a status must never be carried by colour alone.
 *
 * The accent colours the ICON, not the note. Status hues are picked for their
 * distinctness as marks, and most of them do not clear 4.5:1 as small text:
 * status-good is 3.27:1 on white, so a green note is a legibility bug however
 * nice it looks. Pass `noteTone` explicitly for the few that do clear it.
 */
const StatTile = ({ label, value, note, accent, noteTone, icon, hero, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      border: `1px solid ${INK.border}`,
      borderRadius: '10px',
      p: { xs: 2, sm: 2.5 },
      height: '100%',
      backgroundColor: hero ? INK.plane : INK.surface,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'border-color 140ms ease',
      '&:hover': onClick ? { borderColor: '#d1d5db' } : undefined,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
      {icon && (
        <Box sx={{ display: 'flex', color: accent || INK.secondary, '& svg': { fontSize: 16 } }}>
          {icon}
        </Box>
      )}
      <Typography sx={{ ...FONT, color: INK.secondary, fontSize: '13px', lineHeight: 1.3 }}>
        {label}
      </Typography>
    </Box>

    <Typography
      sx={{
        ...FONT,
        color: INK.primary,
        fontSize: hero ? { xs: '32px', sm: '40px' } : { xs: '24px', sm: '28px' },
        fontWeight: 600,
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
      }}
    >
      {typeof value === 'number' ? value.toLocaleString() : value}
    </Typography>

    {note && (
      <Typography sx={{ ...FONT, color: noteTone || INK.muted, fontSize: '12px', mt: 0.5, lineHeight: 1.35 }}>
        {note}
      </Typography>
    )}
  </Box>
);

export default StatTile;
